import { Mat4 } from '../math/mat4';
import { Vec3 } from '../math/vec3';
import { MeshData } from '../geo/mesh';
import { Camera } from './Camera';
import { GpuMesh } from './GpuMesh';
import { Program, RenderTarget, createRenderTarget, destroyRenderTarget, createShadowMap } from './gl';
import { UBER_VS, UBER_FS, SHADOW_FS, POINT_SHADOW_FS, FULLSCREEN_VS, SKY_FS, POST_FS } from './shaders';

export type RGB = [number, number, number];

/** One thing to draw this frame. Renderables build these from their transform + material. */
export interface DrawItem {
  mesh: GpuMesh;
  /** World matrix; ignored for instanced meshes. */
  model: Mat4;
  emissiveMul?: number;
  flash?: number;
  alpha?: number;
  wind?: number;
  castShadow?: boolean;
  transparent?: boolean;
  instanced?: boolean;
  /** Skip frustum culling (e.g. huge terrain that is always partly visible). */
  noCull?: boolean;
  ambientMul?: number;
}

/** Per-frame lighting/atmosphere. Biomes crossfade these. */
export interface FrameParams {
  camera: Camera;
  sunDir: Vec3;
  sunColor: RGB;
  skyColor: RGB;
  groundColor: RGB;
  fogColor: RGB;
  fogNear: number;
  fogFar: number;
  skyTop: RGB;
  skyHorizon: RGB;
  skyBottom: RGB;
  sunDisc: number;
  /** Center of the sun shadow's ortho box (usually the player). */
  shadowCenter: Vec3;
  shadowExtent: number;
  shadowsEnabled: boolean;
  pointLight: { pos: Vec3; color: RGB; radius: number; shadows: boolean } | null;
  time: number;
  post: { vignette: number; fade: number; white: number; heat: number; outline: number; desat: number; tint: RGB };
}

export function defaultFrameParams(camera: Camera): FrameParams {
  return {
    camera,
    sunDir: new Vec3(0.4, 0.8, 0.3).normalize(),
    sunColor: [1.0, 0.95, 0.85],
    skyColor: [0.5, 0.6, 0.75],
    groundColor: [0.25, 0.22, 0.18],
    fogColor: [0.8, 0.85, 0.9],
    fogNear: 40,
    fogFar: 160,
    skyTop: [0.35, 0.55, 0.9],
    skyHorizon: [0.85, 0.85, 0.9],
    skyBottom: [0.4, 0.4, 0.45],
    sunDisc: 1,
    shadowCenter: new Vec3(),
    shadowExtent: 45,
    shadowsEnabled: true,
    pointLight: null,
    time: 0,
    post: { vignette: 0.6, fade: 0, white: 0, heat: 0, outline: 0.9, desat: 0, tint: [1, 1, 1] },
  };
}

const SHADOW_SIZE = 2048;
const POINT_SHADOW_SIZE = 512;

export class Renderer {
  readonly gl: WebGL2RenderingContext;
  readonly uber: Program;
  readonly shadowProg: Program;
  readonly pointShadowProg: Program;
  readonly sky: Program;
  readonly post: Program;
  private target: RenderTarget;
  private readonly shadow: { fbo: WebGLFramebuffer; tex: WebGLTexture; size: number };
  private pointShadow: { fbo: WebGLFramebuffer; tex: WebGLTexture } | null = null;
  private readonly fsVao: WebGLVertexArrayObject;
  private readonly shadowVP = new Mat4();
  private readonly lightView = new Mat4();
  private readonly lightProj = new Mat4();
  width = 1;
  height = 1;
  pixelRatio = 1;
  stats = { drawCalls: 0, triangles: 0, culled: 0 };
  private readonly identity = new Mat4();
  private readonly tmpV = new Vec3();

  constructor(readonly canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', { antialias: false, alpha: false, depth: true, stencil: false, powerPreference: 'high-performance' });
    if (!gl) throw new Error('WebGL2 not available');
    this.gl = gl;
    this.uber = new Program(gl, UBER_VS, UBER_FS, 'uber');
    this.shadowProg = new Program(gl, UBER_VS, SHADOW_FS, 'shadow');
    this.pointShadowProg = new Program(gl, UBER_VS, POINT_SHADOW_FS, 'pointShadow');
    this.sky = new Program(gl, FULLSCREEN_VS, SKY_FS, 'sky');
    this.post = new Program(gl, FULLSCREEN_VS, POST_FS, 'post');
    this.fsVao = gl.createVertexArray()!;
    this.shadow = createShadowMap(gl, SHADOW_SIZE);
    this.target = createRenderTarget(gl, 2, 2);
    this.resize();
    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const w = Math.max(1, Math.floor(this.canvas.clientWidth * dpr));
    const h = Math.max(1, Math.floor(this.canvas.clientHeight * dpr));
    if (w === this.width && h === this.height) return;
    this.width = w; this.height = h; this.pixelRatio = dpr;
    this.canvas.width = w; this.canvas.height = h;
    destroyRenderTarget(this.gl, this.target);
    this.target = createRenderTarget(this.gl, w, h);
  }

  createMesh(data: MeshData, name?: string): GpuMesh { return new GpuMesh(this.gl, data, name); }

  private ensurePointShadow(): { fbo: WebGLFramebuffer; tex: WebGLTexture } {
    if (this.pointShadow) return this.pointShadow;
    const gl = this.gl;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, tex);
    for (let f = 0; f < 6; f++) {
      gl.texImage2D(gl.TEXTURE_CUBE_MAP_POSITIVE_X + f, 0, gl.DEPTH_COMPONENT24, POINT_SHADOW_SIZE, POINT_SHADOW_SIZE, 0, gl.DEPTH_COMPONENT, gl.UNSIGNED_INT, null);
    }
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_COMPARE_MODE, gl.COMPARE_REF_TO_TEXTURE);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_COMPARE_FUNC, gl.LEQUAL);
    const fbo = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.drawBuffers([gl.NONE]);
    gl.readBuffer(gl.NONE);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this.pointShadow = { fbo, tex };
    return this.pointShadow;
  }

  private drawMesh(item: DrawItem, prog: Program): void {
    const gl = this.gl;
    const mesh = item.mesh;
    gl.bindVertexArray(mesh.vao);
    if (item.instanced) {
      if (mesh.instanceCount === 0) return;
      gl.uniform1i(prog.loc('uInstanced'), 1);
      gl.drawElementsInstanced(gl.TRIANGLES, mesh.indexCount, gl.UNSIGNED_INT, 0, mesh.instanceCount);
      this.stats.triangles += (mesh.indexCount / 3) * mesh.instanceCount;
    } else {
      gl.uniform1i(prog.loc('uInstanced'), 0);
      gl.uniformMatrix4fv(prog.loc('uModel'), false, item.model.e);
      gl.drawElements(gl.TRIANGLES, mesh.indexCount, gl.UNSIGNED_INT, 0);
      this.stats.triangles += mesh.indexCount / 3;
    }
    this.stats.drawCalls++;
  }

  private visible(item: DrawItem, cam: Camera): boolean {
    if (item.noCull) return true;
    const m = item.mesh;
    if (item.instanced) return cam.sphereVisible(m.instCenter.x, m.instCenter.y, m.instCenter.z, m.instRadius);
    const c = item.model.transformPoint(this.tmpV.copy(m.center));
    const r = m.radius * item.model.maxScale();
    return cam.sphereVisible(c.x, c.y, c.z, r);
  }

  private setupShadowMatrices(f: FrameParams): void {
    const ext = f.shadowExtent;
    const c = f.shadowCenter;
    const eye = this.tmpV.copy(c).addScaled(f.sunDir, 120);
    this.lightView.lookAt(eye, c, Math.abs(f.sunDir.y) > 0.99 ? new Vec3(0, 0, 1) : Vec3.UP);
    // snap the ortho window to shadow texels to avoid edge shimmer while moving
    const texel = (2 * ext) / SHADOW_SIZE;
    const lc = this.lightView.transformPoint(c.clone());
    const sx = Math.round(lc.x / texel) * texel - lc.x;
    const sy = Math.round(lc.y / texel) * texel - lc.y;
    this.lightProj.ortho(-ext + sx, ext + sx, -ext + sy, ext + sy, 1, 260);
    this.shadowVP.multiplyMatrices(this.lightProj, this.lightView);
  }

  private renderSunShadow(items: DrawItem[], f: FrameParams): void {
    const gl = this.gl;
    this.setupShadowMatrices(f);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.shadow.fbo);
    gl.viewport(0, 0, this.shadow.size, this.shadow.size);
    gl.clear(gl.DEPTH_BUFFER_BIT);
    gl.colorMask(false, false, false, false);
    gl.enable(gl.POLYGON_OFFSET_FILL);
    gl.polygonOffset(1.5, 3.0);
    const p = this.shadowProg.use();
    p.m4('uProj', this.lightProj.e);
    p.m4('uView', this.lightView.e);
    p.f('uTime', f.time);
    for (const it of items) {
      if (it.castShadow === false || it.transparent) continue;
      p.f('uWind', it.wind ?? 0);
      this.drawMesh(it, p);
    }
    gl.disable(gl.POLYGON_OFFSET_FILL);
    gl.colorMask(true, true, true, true);
  }

  private renderPointShadow(items: DrawItem[], f: FrameParams): void {
    const pl = f.pointLight!;
    const gl = this.gl;
    const ps = this.ensurePointShadow();
    const far = pl.radius * 1.05;
    const proj = new Mat4().perspective(Math.PI / 2, 1, 0.05, far);
    const dirs: [Vec3, Vec3][] = [
      [new Vec3(1, 0, 0), new Vec3(0, -1, 0)], [new Vec3(-1, 0, 0), new Vec3(0, -1, 0)],
      [new Vec3(0, 1, 0), new Vec3(0, 0, 1)], [new Vec3(0, -1, 0), new Vec3(0, 0, -1)],
      [new Vec3(0, 0, 1), new Vec3(0, -1, 0)], [new Vec3(0, 0, -1), new Vec3(0, -1, 0)],
    ];
    gl.bindFramebuffer(gl.FRAMEBUFFER, ps.fbo);
    gl.viewport(0, 0, POINT_SHADOW_SIZE, POINT_SHADOW_SIZE);
    gl.colorMask(false, false, false, false);
    const p = this.pointShadowProg.use();
    p.m4('uProj', proj.e);
    p.f('uTime', f.time);
    p.v3('uPointPos', pl.pos.x, pl.pos.y, pl.pos.z);
    p.f('uPointFar', far);
    const view = new Mat4();
    const tgt = new Vec3();
    for (let face = 0; face < 6; face++) {
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_CUBE_MAP_POSITIVE_X + face, ps.tex, 0);
      gl.clear(gl.DEPTH_BUFFER_BIT);
      view.lookAt(pl.pos, tgt.copy(pl.pos).add(dirs[face][0]), dirs[face][1]);
      p.m4('uView', view.e);
      for (const it of items) {
        if (it.castShadow === false || it.transparent) continue;
        // only things near the light matter (bounding-sphere test, also for noCull terrain)
        if (it.instanced) {
          if (it.mesh.instCenter.distanceTo(pl.pos) > far + it.mesh.instRadius) continue;
        } else {
          const c = it.model.transformPoint(this.tmpV.copy(it.mesh.center));
          if (c.distanceTo(pl.pos) > far + it.mesh.radius * it.model.maxScale()) continue;
        }
        p.f('uWind', it.wind ?? 0);
        this.drawMesh(it, p);
      }
    }
    gl.colorMask(true, true, true, true);
  }

  render(items: DrawItem[], f: FrameParams): void {
    const gl = this.gl;
    const cam = f.camera;
    this.stats.drawCalls = 0; this.stats.triangles = 0; this.stats.culled = 0;
    cam.aspect = this.width / this.height;
    cam.update();

    if (f.shadowsEnabled) this.renderSunShadow(items, f);
    const pointShadowOn = !!(f.pointLight && f.pointLight.shadows);
    if (pointShadowOn) this.renderPointShadow(items, f);

    // ---- main pass ----
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.target.fbo);
    gl.viewport(0, 0, this.width, this.height);
    gl.clearColor(f.fogColor[0], f.fogColor[1], f.fogColor[2], 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.depthMask(true);
    gl.disable(gl.BLEND);

    const p = this.uber.use();
    p.m4('uProj', cam.proj.e);
    p.m4('uView', cam.view.e);
    p.m4('uShadowVP', this.shadowVP.e);
    p.f('uTime', f.time);
    p.v3('uSunDir', f.sunDir.x, f.sunDir.y, f.sunDir.z);
    p.v3a('uSunColor', f.sunColor);
    p.v3a('uSkyColor', f.skyColor);
    p.v3a('uGroundColor', f.groundColor);
    p.v3a('uFogColor', f.fogColor);
    p.v2('uFogRange', f.fogNear, f.fogFar);
    p.v3('uCamPos', cam.position.x, cam.position.y, cam.position.z);
    p.f('uShadowOn', f.shadowsEnabled ? 1 : 0);
    p.f('uShadowTexel', 1 / SHADOW_SIZE);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.shadow.tex);
    p.i('uShadowMap', 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, pointShadowOn ? this.pointShadow!.tex : this.ensurePointShadow().tex);
    p.i('uPointShadow', 1);
    if (f.pointLight) {
      const pl = f.pointLight;
      p.v3('uPointPos', pl.pos.x, pl.pos.y, pl.pos.z);
      p.v3a('uPointColor', pl.color);
      p.f('uPointRadius', pl.radius);
      p.f('uPointShadowOn', pointShadowOn ? 1 : 0);
      p.f('uPointFar', pl.radius * 1.05);
    } else {
      p.f('uPointRadius', 0);
      p.f('uPointShadowOn', 0);
    }

    const transparent: { item: DrawItem; dist: number }[] = [];
    for (const it of items) {
      if (!this.visible(it, cam)) { this.stats.culled++; continue; }
      if (it.transparent) {
        const c = it.instanced ? it.mesh.instCenter : it.model.transformPoint(this.tmpV.copy(it.mesh.center));
        transparent.push({ item: it, dist: c.distanceToSq(cam.position) });
        continue;
      }
      this.setMaterial(p, it);
      this.drawMesh(it, p);
    }

    // ---- sky (depth = 1, LEQUAL passes where nothing was drawn) ----
    const s = this.sky.use();
    s.m4('uInvVP', cam.invViewProj.e);
    s.v3('uCamPos', cam.position.x, cam.position.y, cam.position.z);
    s.v3a('uSkyTop', f.skyTop);
    s.v3a('uSkyHorizon', f.skyHorizon);
    s.v3a('uSkyBottom', f.skyBottom);
    s.v3('uSunDir', f.sunDir.x, f.sunDir.y, f.sunDir.z);
    s.v3a('uSunColor', f.sunColor);
    s.f('uSunDisc', f.sunDisc);
    gl.depthMask(false);
    gl.bindVertexArray(this.fsVao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    this.stats.drawCalls++;

    // ---- transparent, back to front ----
    if (transparent.length) {
      transparent.sort((a, b) => b.dist - a.dist);
      this.uber.use();
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.disable(gl.CULL_FACE);
      for (const t of transparent) {
        this.setMaterial(p, t.item);
        this.drawMesh(t.item, p);
      }
      gl.enable(gl.CULL_FACE);
      gl.disable(gl.BLEND);
    }
    gl.depthMask(true);

    // ---- post ----
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.width, this.height);
    gl.disable(gl.DEPTH_TEST);
    const q = this.post.use();
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, this.target.color); q.i('uColor', 0);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, this.target.normal); q.i('uNormal', 1);
    gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, this.target.depth); q.i('uDepth', 2);
    q.v2('uResolution', this.width, this.height);
    q.v2('uNearFar', cam.near, cam.far);
    q.f('uTime', f.time);
    q.f('uVignette', f.post.vignette);
    q.f('uFade', f.post.fade);
    q.f('uWhite', f.post.white);
    q.f('uHeat', f.post.heat);
    q.f('uOutline', f.post.outline);
    q.f('uDesat', f.post.desat);
    q.v3a('uTint', f.post.tint);
    q.v2('uFogRange', f.fogNear, f.fogFar);
    gl.bindVertexArray(this.fsVao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.enable(gl.DEPTH_TEST);
    gl.bindVertexArray(null);
    // unbind textures used as both attachment and sampler before the next frame
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, null);
  }

  private setMaterial(p: Program, it: DrawItem): void {
    p.f('uEmissiveMul', it.emissiveMul ?? 1);
    p.f('uFlash', it.flash ?? 0);
    p.f('uAlpha', it.alpha ?? 1);
    p.f('uWind', it.wind ?? 0);
    p.f('uAmbientMul', it.ambientMul ?? 1);
  }

  /** Read back the current canvas as a PNG data URL (debug screenshots). */
  screenshot(): string { return this.canvas.toDataURL('image/png'); }
}
